/* ═══════════════════════════════════════════════════════════════
   PiARIS — Audit Issue Card
   Rich per-issue display matching a professional audit report format:
   #NN title | status badges | component | elements | fix | related |
   screenshots | right-sidebar (Priority/Risk/Category/Affected Users/
   Guidelines/Client Status/Created/Updated)

   Document-like, print-friendly, dark/light mode safe, ADA compliant.
   © 2026 Pi Consultants · pi-consulting.us
   ═══════════════════════════════════════════════════════════════ */
import React from "react";
import { P, W, WCAG_KB, FC } from "../constants";
import { sevToPriority, wcagToAffectedUsers, wcagToRiskLevel,
  selectorToComponent, isSitewideIssue, classifyFixCategory,
  fixCategoryLabel, TRACKER_STATUSES, defaultIssueTrackerFields } from "../helpers";
import { IssueScreenshots } from "./IssueScreenshots";

var E = React.createElement;

/* Affected-user icon (small SVG) */
function UserGroupIcon(group) {
  var c = P.slateM;
  if (group === "Visual")          return E("span", { "aria-hidden": "true", style: { fontSize: 10 } }, "👁️");
  if (group === "Auditory")        return E("span", { "aria-hidden": "true", style: { fontSize: 10 } }, "👂️");
  if (group === "Cognitive")       return E("span", { "aria-hidden": "true", style: { fontSize: 10 } }, "🧠");
  if (group === "Motor/Dexterity") return E("span", { "aria-hidden": "true", style: { fontSize: 10 } }, "✋");
  if (group === "UX")              return E("span", { "aria-hidden": "true", style: { fontSize: 10 } }, "✨");
  return null;
}

/** Priority pill — color per P1/P2/P3, matches Allyant convention. */
function PriorityPill(p) {
  var pri = p.priority;
  var col = pri.color === "sCrit"   ? P.sCrit
         : pri.color === "amber"   ? P.amber
         : P.emerald;
  var bg  = pri.color === "sCrit"   ? P.sCritBg
         : pri.color === "amber"   ? P.sMajBg
         : P.emeraldLt;
  return E("span", {
    "aria-label": pri.label + " priority, level " + pri.code,
    style: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "3px 10px",
      borderRadius: 6,
      background: bg,
      color: col,
      fontSize: 10,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: ".04em",
      border: "1px solid " + col + "40"
    }
  }, pri.label.toUpperCase() + " (" + pri.code + ")");
}

/** Row label + value — used in sidebar */
function SideRow(p) {
  return E("div", { style: { display: "flex", flexDirection: "column", gap: 2, marginBottom: 10 } },
    E("div", {
      style: {
        fontSize: 9,
        fontWeight: 800,
        color: P.slateL,
        textTransform: "uppercase",
        letterSpacing: ".06em"
      }
    }, p.label),
    E("div", { style: { fontSize: 11, color: P.slate, lineHeight: 1.45 } }, p.children));
}

/* ═══════════════════════════════════════════════════════════════
   TRACKER CONTROLS — PR-D
   Per-issue Assignee / Status / Notes controls with debounced
   POSTs to /api/issue-tracker. Optimistic local state; reverts
   to pre-edit values on 5xx with an inline error. Tolerates
   undefined initial fields (legacy issues from pre-PR-D scans).
   ═══════════════════════════════════════════════════════════════ */

/* Format "Updated 2s ago" / "Updated 5m ago" / "Updated 3h ago".
   Re-evaluated on each render — fine because the parent re-renders
   whenever the issue mutates. */
function formatRelative(iso) {
  if (!iso) return "";
  var t;
  try { t = new Date(iso).getTime(); } catch (e) { return ""; }
  if (!t || isNaN(t)) return "";
  var diff = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diff < 5)   return "just now";
  if (diff < 60)  return diff + "s ago";
  if (diff < 3600) return Math.floor(diff / 60) + "m ago";
  if (diff < 86400) return Math.floor(diff / 3600) + "h ago";
  return Math.floor(diff / 86400) + "d ago";
}

function TrackerControls(props) {
  /* Seed local state from issue + defaults so legacy field-less
     issues still render the controls with the spec defaults. */
  var d = defaultIssueTrackerFields();
  var iss = props.issue || {};
  var _assignee = React.useState(iss.assignee != null ? iss.assignee : d.assignee);
  var assignee = _assignee[0], setAssignee = _assignee[1];
  var _status = React.useState(iss.status || d.status);
  var status = _status[0], setStatus = _status[1];
  var _notes = React.useState(iss.notes != null ? iss.notes : d.notes);
  var notes = _notes[0], setNotes = _notes[1];
  var _updatedAt = React.useState(iss.trackerUpdatedAt || null);
  var updatedAt = _updatedAt[0], setUpdatedAt = _updatedAt[1];
  var _error = React.useState(null);
  var error = _error[0], setError = _error[1];

  /* Per-field debounce timers — we keep them in refs so the cleanup
     can clear without re-creating the timer on every keystroke. */
  var assigneeTimer = React.useRef(null);
  var notesTimer = React.useRef(null);

  /* Pre-edit shadow refs so a 5xx revert can restore the value the
     user mutated (not the value the next field changed to). */
  var prevAssignee = React.useRef(iss.assignee != null ? iss.assignee : d.assignee);
  var prevStatus = React.useRef(iss.status || d.status);
  var prevNotes = React.useRef(iss.notes != null ? iss.notes : d.notes);

  /* Re-sync when the parent passes a fresh issue object (e.g. after a
     scan reload). Skips when the parent is just re-rendering with the
     same issue — we never want to clobber the user's in-flight edits. */
  React.useEffect(function () {
    if (!iss) return;
    if (iss.assignee !== undefined && iss.assignee !== prevAssignee.current) {
      setAssignee(iss.assignee);
      prevAssignee.current = iss.assignee;
    }
    if (iss.status && iss.status !== prevStatus.current) {
      setStatus(iss.status);
      prevStatus.current = iss.status;
    }
    if (iss.notes !== undefined && iss.notes !== prevNotes.current) {
      setNotes(iss.notes);
      prevNotes.current = iss.notes;
    }
    if (iss.trackerUpdatedAt && iss.trackerUpdatedAt !== updatedAt) {
      setUpdatedAt(iss.trackerUpdatedAt);
    }
  }, [iss.assignee, iss.status, iss.notes, iss.trackerUpdatedAt]);

  /* Cleanup pending timers on unmount so a delayed POST doesn't fire
     after the card is gone. */
  React.useEffect(function () {
    return function () {
      if (assigneeTimer.current) clearTimeout(assigneeTimer.current);
      if (notesTimer.current) clearTimeout(notesTimer.current);
    };
  }, []);

  /* Single-field POST. Returns a promise so callers can await. On 5xx
     we revert the supplied prevValue + show an inline error; 4xx
     leaves the optimistic value alone but surfaces the error text so
     the operator can fix the bad input. */
  function postPatch(patch, revert) {
    if (!props.sessionId || !props.issueId) {
      /* No session context (e.g. exported read-only report) — just
         pretend we saved; the parent shouldn't have rendered us. */
      return Promise.resolve();
    }
    var body = Object.assign({
      sessionId: props.sessionId,
      issueId:   props.issueId
    }, patch);
    if (props.pageIndex !== undefined && props.pageIndex !== null) {
      body.pageIndex = props.pageIndex;
    }
    setError(null);
    return fetch("/api/issue-tracker", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) {
        return r.text().then(function (txt) {
          var msg = "Save failed (" + r.status + ")";
          try { var j = JSON.parse(txt); if (j && j.error) msg = j.error; } catch (_) {}
          if (r.status >= 500 && typeof revert === "function") revert();
          setError(msg);
          throw new Error(msg);
        });
      }
      return r.json();
    }).then(function (resp) {
      if (resp && resp.issue) {
        if (resp.issue.trackerUpdatedAt) setUpdatedAt(resp.issue.trackerUpdatedAt);
        /* Refresh the parent-known shadows so subsequent edits
           don't think the server is stale. */
        if (resp.issue.assignee !== undefined) prevAssignee.current = resp.issue.assignee;
        if (resp.issue.status) prevStatus.current = resp.issue.status;
        if (resp.issue.notes !== undefined) prevNotes.current = resp.issue.notes;
      }
    }).catch(function (e) {
      /* fetch network error — treat like a 5xx revert */
      if (typeof revert === "function") revert();
      setError(e.message || "Network error");
    });
  }

  function onAssigneeChange(e) {
    var v = e.target.value;
    var prior = prevAssignee.current;
    setAssignee(v);
    if (assigneeTimer.current) clearTimeout(assigneeTimer.current);
    assigneeTimer.current = setTimeout(function () {
      prevAssignee.current = v;
      postPatch({ assignee: v }, function () {
        setAssignee(prior);
        prevAssignee.current = prior;
      });
    }, 500);
  }

  function onStatusChange(e) {
    /* Status is small / discrete — fire immediately, no debounce. */
    var v = e.target.value;
    var prior = prevStatus.current;
    setStatus(v);
    prevStatus.current = v;
    postPatch({ status: v }, function () {
      setStatus(prior);
      prevStatus.current = prior;
    });
  }

  function onNotesChange(e) {
    var v = e.target.value;
    var prior = prevNotes.current;
    setNotes(v);
    if (notesTimer.current) clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(function () {
      prevNotes.current = v;
      postPatch({ notes: v }, function () {
        setNotes(prior);
        prevNotes.current = prior;
      });
    }, 500);
  }

  var inputStyle = {
    fontFamily: "inherit",
    fontSize: 11,
    padding: "5px 8px",
    borderRadius: 6,
    border: "1px solid " + P.bdr,
    background: P.card,
    color: P.slate,
    width: "100%",
    minHeight: 28
  };
  var labelStyle = {
    fontSize: 9,
    fontWeight: 800,
    color: P.slateM,
    textTransform: "uppercase",
    letterSpacing: ".06em",
    marginBottom: 3,
    display: "block"
  };

  return E("div", {
    "aria-label": "Remediation tracker",
    style: {
      marginTop: 12,
      paddingTop: 10,
      borderTop: "1px dashed " + P.bdr
    }
  },
    E("div", {
      style: {
        fontSize: 10, fontWeight: 800, color: P.slate, textTransform: "uppercase",
        letterSpacing: ".04em", marginBottom: 8
      }
    }, "Tracker"),
    /* Row 1: assignee (flex 2) + status (flex 1). Collapses to stacked at <600px via inline media query proxy: we just allow wrap. */
    E("div", { style: { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 8, alignItems: "flex-start" } },
      E("div", { style: { flex: "2 1 200px", minWidth: 0 } },
        E("label", { htmlFor: "tracker-assignee-" + props.issueId, style: labelStyle }, "Assignee"),
        E("input", {
          id: "tracker-assignee-" + props.issueId,
          type: "text",
          value: assignee || "",
          placeholder: "team / vendor",
          onChange: onAssigneeChange,
          style: inputStyle,
          "aria-label": "Assignee"
        })
      ),
      E("div", { style: { flex: "1 1 130px", minWidth: 0 } },
        E("label", { htmlFor: "tracker-status-" + props.issueId, style: labelStyle }, "Status"),
        E("select", {
          id: "tracker-status-" + props.issueId,
          value: status || "To Do",
          onChange: onStatusChange,
          style: inputStyle,
          "aria-label": "Status"
        }, TRACKER_STATUSES.map(function (s) {
          return E("option", { key: s, value: s }, s);
        }))
      )
    ),
    /* Row 2: notes textarea full width */
    E("div", null,
      E("label", { htmlFor: "tracker-notes-" + props.issueId, style: labelStyle }, "Notes"),
      E("textarea", {
        id: "tracker-notes-" + props.issueId,
        rows: 2,
        value: notes || "",
        onChange: onNotesChange,
        style: Object.assign({}, inputStyle, { resize: "vertical", minHeight: 48, fontFamily: "inherit" }),
        "aria-label": "Notes"
      })
    ),
    /* Status line: error wins; otherwise show updated timestamp. */
    error ? E("div", {
      role: "alert",
      style: { fontSize: 10, color: P.sCrit, marginTop: 6 }
    }, error)
    : updatedAt ? E("div", {
      style: { fontSize: 10, color: P.slateL, marginTop: 6, fontStyle: "italic" }
    }, "Updated " + formatRelative(updatedAt))
    : null
  );
}

/** AuditIssueCard — the headline component.
    Props:
      issue: the raw issue object from wp[pi].issues[ii]
      issueNumber: global #NN (from globalIssueNumbers map)
      pageIdx, issIdx: indices for keying + accept handlers
      templateStats: for sitewide derivation
      screenshot: optional { src, alt } to display below fix
      darkMode: boolean */
function AuditIssueCard(p) {
  var iss = p.issue;
  var num = p.issueNumber;
  var ts = p.templateStats;
  var wcag = iss.wcag || "";
  var wcagInfo = W[wcag] || {};
  var kbInfo = WCAG_KB[wcag] || {};
  var priority = sevToPriority(iss.sev || "major");
  var sitewide = isSitewideIssue(iss, ts);
  var affected = wcagToAffectedUsers(wcag);
  var risk = wcagToRiskLevel(iss.sev, wcagInfo.l);
  var category = fixCategoryLabel(classifyFixCategory(iss));
  var component = selectorToComponent(iss.selector, iss.el);
  var isOpen = !iss.remediated;
  var statusColor = isOpen ? P.sMin : P.emerald;
  var statusBg = isOpen ? P.sMinBg : P.emeraldLt;

  /* Derive element descriptions. Prefer nodes[] (all instances), fall back to
     single selector/el. Extension may store nodes: [{selector, el}, ...] for
     every offending element — that's the richest source. */
  var elementItems = (function() {
    if (Array.isArray(iss.nodes) && iss.nodes.length > 0) {
      return iss.nodes.slice(0, 10).map(function(n) {
        var label = n.selector || n.el || "";
        if (!label) return null;
        return label.length > 120 ? label.substring(0, 120) + "…" : label;
      }).filter(Boolean);
    }
    if (iss.selector && iss.selector.length > 0) return [iss.selector];
    if (iss.el) return [iss.el.substring(0, 120) + (iss.el.length > 120 ? "…" : "")];
    return [];
  })();
  var elementProse = elementItems.length > 0 ? elementItems[0] : "—";
  var extraNodes = Math.max(0, (iss.nodes && iss.nodes.length || 0) - elementItems.length);

  /* Fix prose: combine our fix + WCAG guidance + KB long-form */
  var fixProse = iss.fix || wcagInfo.r || kbInfo.fix || "";
  var relatedArticle = kbInfo.url ? { label: (kbInfo.n || wcag) + " — W3C Understanding", url: kbInfo.url } : null;

  /* PR-D: stable issue id for the tracker endpoint. Fall back to
     fingerprint or the global issue number so legacy issues without
     ids still get a stable handle for the session. */
  var trackerIssueId = iss.id || iss.fingerprint || ("iss-" + p.issueNumber);

  return E("article", {
    className: "auditIssueCard",
    role: "article",
    "aria-labelledby": "iss-" + p.pageIdx + "-" + p.issIdx + "-title",
    style: {
      background: P.card,
      border: "1px solid " + P.bdr,
      borderRadius: 12,
      padding: "18px 22px",
      marginBottom: 14,
      display: "grid",
      gridTemplateColumns: "minmax(0,1fr) 190px",
      gap: 20,
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      position: "relative"
    }
  },
    /* ── LEFT: title + body ── */
    E("div", { style: { minWidth: 0 } },
      /* Title row — #NN: <title> */
      E("h3", {
        id: "iss-" + p.pageIdx + "-" + p.issIdx + "-title",
        style: {
          fontSize: 14,
          fontWeight: 800,
          lineHeight: 1.4,
          margin: "0 0 10px",
          color: P.slate
        }
      },
        E("span", {
          style: { color: P.slateL, fontWeight: 700, marginRight: 4, fontFeatureSettings: '"tnum"' }
        }, "#" + num + ":"),
        " ",
        iss.desc || wcagInfo.n || "Accessibility issue"
      ),
      /* Status + sitewide badges */
      E("div", { style: { display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12, alignItems: "center" } },
        E("span", {
          "aria-label": isOpen ? "Status open" : "Status remediated",
          style: {
            padding: "3px 10px",
            borderRadius: 6,
            background: statusBg,
            color: statusColor,
            fontSize: 10,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: ".04em",
            border: "1px solid " + statusColor + "40"
          }
        }, isOpen ? "OPEN" : "REMEDIATED"),
        E(PriorityPill, { priority: priority }),
        sitewide ? E("span", {
          "aria-label": "Sitewide issue",
          style: {
            padding: "3px 10px",
            borderRadius: 6,
            background: P.priLt,
            color: P.pri,
            fontSize: 10,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: ".04em",
            border: "1px dashed " + P.pri + "60"
          }
        }, "🌐 Sitewide") : null,
        risk === "High Risk" ? E("span", {
          "aria-label": "High risk issue",
          style: {
            padding: "3px 10px",
            borderRadius: 6,
            background: P.sCritBg,
            color: P.sCrit,
            fontSize: 10,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: ".04em",
            border: "1px solid " + P.sCrit + "40"
          }
        }, "⚠ High Risk") : null
      ),
      /* Component row */
      component.name ? E("div", {
        style: { fontSize: 11, color: P.slateM, marginBottom: 10, lineHeight: 1.5 }
      },
        E("span", { style: { fontWeight: 800, color: P.slate, textTransform: "uppercase", letterSpacing: ".04em", fontSize: 10, marginRight: 6 } }, "Component:"),
        E("span", { style: { color: P.slate, fontWeight: 700 } }, component.name),
        component.kind ? E("span", { style: { color: P.slateL, fontWeight: 500 } }, " (" + component.kind + (sitewide ? ", sitewide" : "") + ")") : null
      ) : null,
      /* Elements section */
      E("div", { style: { marginBottom: 12 } },
        E("div", { style: { fontSize: 10, fontWeight: 800, color: P.slate, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 6 } },
          "Elements" + (iss.count && iss.count > 1 ? " (×" + iss.count + ")" : "")),
        elementItems.length > 0
          ? E("ul", { style: { margin: 0, paddingLeft: 18, fontSize: 11, color: P.slateM, lineHeight: 1.6 } },
              elementItems.map(function(txt, ei) {
                return E("li", { key: ei, style: { marginBottom: 2 } },
                  E("code", {
                    style: {
                      fontFamily: FC,
                      fontSize: 10.5,
                      background: P.bgS,
                      padding: "1px 5px",
                      borderRadius: 3,
                      color: P.slate,
                      wordBreak: "break-all"
                    }
                  }, txt)
                );
              }).concat(extraNodes > 0 ? [
                E("li", { key: "more", style: { listStyle: "none", color: P.slateL, fontSize: 10, fontStyle: "italic", marginTop: 2 } },
                  "+ " + extraNodes + " more element" + (extraNodes === 1 ? "" : "s"))
              ] : [])
            )
          : E("div", { style: { fontSize: 11, color: P.slateL, fontStyle: "italic", padding: "4px 0" } },
              "Element selector not captured — re-scan with the latest extension to capture DOM locations")
      ),
      /* Fix section */
      E("div", { style: { marginBottom: 12 } },
        E("div", { style: { fontSize: 10, fontWeight: 800, color: P.slate, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 6 } }, "Fix"),
        E("div", {
          style: { fontSize: 11.5, color: P.slateM, lineHeight: 1.65, whiteSpace: "pre-wrap" }
        }, fixProse || "No remediation guidance available for this issue type.")
      ),
      /* v1.0: Pi Consultants AI Review block — rendered when iss._aiReview
         is present (populated by loadPageDetail when the server returned an
         aiReview object on the page doc). Honest badge for verified/false-
         positive, plain-English description, user impact, effort bucket,
         and the Pi positioning call-to-action. */
      iss._aiReview ? (function(){
        var r = iss._aiReview;
        var verified = r.verified !== false;
        var confPct = Math.round((r.confidence || 0) * 100);
        var effortColors = {
          trivial: P.emerald, low: P.emerald,
          medium: P.amber, high: P.coral
        };
        var effortCol = effortColors[r.remediationEffort] || P.slateM;
        return E("div", {
          style: {
            marginBottom: 12,
            padding: "12px 14px",
            borderRadius: 10,
            background: verified ? "linear-gradient(135deg,#EEF2FF,#F0F9FF)" : P.amberLt + "40",
            border: "1px solid " + (verified ? P.pri + "30" : P.amber + "50")
          }
        },
          E("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" } },
            E("span", { style: { fontSize: 13 }, "aria-hidden": "true" }, "🎯"),
            E("span", { style: { fontSize: 10, fontWeight: 800, color: P.pri, textTransform: "uppercase", letterSpacing: ".05em" } }, "Pi Consultants AI Review"),
            verified
              ? E("span", { style: { padding: "2px 8px", borderRadius: 6, background: P.emeraldLt, color: P.emerald, fontSize: 9, fontWeight: 700 } }, "✓ VERIFIED " + confPct + "%")
              : E("span", { style: { padding: "2px 8px", borderRadius: 6, background: P.amberLt, color: P.amber, fontSize: 9, fontWeight: 700 } }, "⚠ LIKELY FALSE POSITIVE"),
            r.remediationEffort ? E("span", { style: { padding: "2px 8px", borderRadius: 6, background: effortCol + "20", color: effortCol, fontSize: 9, fontWeight: 700, textTransform: "uppercase" } },
              r.remediationEffort + " effort" + (r.estimatedMinutes ? " · ~" + r.estimatedMinutes + "m" : "")
            ) : null
          ),
          /* Plain-English description */
          r.plainDescription ? E("div", {
            style: { fontSize: 12, color: P.slate, lineHeight: 1.6, marginBottom: 6, fontWeight: 500 }
          }, r.plainDescription) : null,
          /* User impact */
          r.userImpact ? E("div", {
            style: { fontSize: 11, color: P.slateM, lineHeight: 1.55, marginBottom: 6, fontStyle: "italic" }
          },
            E("span", { "aria-hidden": "true", style: { marginRight: 4 } }, "👥 "),
            r.userImpact
          ) : null,
          /* False-positive reason (if not verified) */
          !verified && r.falsePositiveReason ? E("div", {
            style: { fontSize: 11, color: P.amber, lineHeight: 1.55, marginBottom: 6, padding: "6px 8px", borderRadius: 6, background: P.amberLt + "60" }
          },
            E("strong", null, "Why we think this is a false positive: "),
            r.falsePositiveReason
          ) : null,
          /* Pi positioning CTA */
          r.piPositioning ? E("div", {
            style: { fontSize: 11, color: P.pri, lineHeight: 1.55, marginTop: 6, paddingTop: 6, borderTop: "1px dashed " + P.pri + "30", fontWeight: 600 }
          },
            E("span", { "aria-hidden": "true", style: { marginRight: 4 } }, "👀 "),
            r.piPositioning
          ) : null,
          /* Budget-skipped footnote */
          r.budgetSkipped ? E("div", {
            style: { fontSize: 9, color: P.slateL, fontStyle: "italic", marginTop: 4 }
          }, "AI review budget cap reached for this scan — contact Piaris.Support@piconsultants.us for full review") : null
        );
      })() : null,
      /* Related articles */
      relatedArticle ? E("div", { style: { marginBottom: 12 } },
        E("div", { style: { fontSize: 10, fontWeight: 800, color: P.slate, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 6 } }, "Related Articles"),
        E("a", {
          href: relatedArticle.url,
          target: "_blank",
          rel: "noopener noreferrer",
          style: {
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            borderRadius: 6,
            border: "1px solid " + P.bdr,
            background: P.bgS,
            color: P.pri,
            fontSize: 11,
            fontWeight: 600,
            textDecoration: "none",
            maxWidth: "100%"
          },
          "aria-label": "Open W3C Understanding article for " + wcag + " in new tab"
        },
          E("span", { "aria-hidden": "true" }, "📄"),
          E("span", { style: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }, relatedArticle.label)
        )
      ) : null,
      /* Screenshot / DOM preview. Prefers a rendered <img> when the extension
         streamed a JPEG thumbnail; falls back to rendering the captured DOM
         snapshot in a sandboxed iframe (v0.6.10 — extension switched from
         screenshots to DOM capture in v0.3.0, so new scans use the iframe
         path). If neither exists, shows a muted placeholder. */
      (p.screenshot && p.screenshot.src) ? E("div", { style: { marginBottom: 4 } },
        E("div", { style: { fontSize: 10, fontWeight: 800, color: P.slate, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 6 } }, "Screenshot"),
        E("figure", { style: { margin: 0, display: "inline-block", maxWidth: "100%" } },
          E("img", {
            src: p.screenshot.src,
            alt: p.screenshot.alt || ("Screenshot of " + (iss.desc || "issue").substring(0, 60)),
            style: {
              maxWidth: "100%",
              maxHeight: 180,
              borderRadius: 6,
              border: "1px solid " + P.bdr,
              display: "block"
            }
          })
        )
      ) : (p.domSnapshot ? E("div", { style: { marginBottom: 4 } },
        E("div", { style: { fontSize: 10, fontWeight: 800, color: P.slate, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 } },
          "Page Preview",
          E("span", { style: { fontSize: 8, fontWeight: 600, color: P.slateL, fontStyle: "italic" } }, "(rendered from DOM snapshot)")),
        E("iframe", {
          title: "Preview of " + (p.page && p.page.n || "page") + " containing issue " + p.issueNumber,
          srcDoc: p.domSnapshot,
          sandbox: "allow-same-origin",
          loading: "lazy",
          style: {
            width: "100%",
            height: 220,
            borderRadius: 6,
            border: "1px solid " + P.bdr,
            background: "#fff",
            pointerEvents: "none"  /* preview only — prevent tab-trap from embedded content */
          }
        })
      ) : null),
      /* ── Issue screenshots (multi-attachment; manual uploads + extension auto-capture)
           Shown only when a sessionId is passed in — read-only mode for exports/reports. */
      p.sessionId ? E(IssueScreenshots, {
        sessionId:   p.sessionId,
        issueId:     trackerIssueId,
        pageIndex:   (p.pageIdx === 0 || p.pageIdx) ? p.pageIdx : null,
        screenshots: iss.screenshots || [],
        /* bbox lets IssueScreenshots lazy-derive a server-side crop from the
           page's rawScreenshotUrl. Prefer nodes[0].bbox (axe/HTMLCS output);
           fall back to iss.bbox if a handler attached one directly. */
        bbox:        (iss.nodes && iss.nodes[0] && iss.nodes[0].bbox) || iss.bbox || null,
        onMutate:    p.onScreenshotsChange || null,
        readOnly:    p.readOnly === true
      }) : null,
      /* ── Remediation tracker (PR-D) — Assignee / Status / Notes.
           Hidden in read-only / exported reports; hidden when there's
           no sessionId to POST to. */
      (p.sessionId && p.readOnly !== true) ? E(TrackerControls, {
        sessionId: p.sessionId,
        issueId:   trackerIssueId,
        pageIndex: (p.pageIdx === 0 || p.pageIdx) ? p.pageIdx : null,
        issue:     iss
      }) : null
    ),
    /* ── RIGHT: metadata sidebar ── */
    E("aside", {
      "aria-label": "Issue metadata",
      style: {
        borderLeft: "1px solid " + P.bdr,
        paddingLeft: 18,
        minWidth: 0
      }
    },
      E(SideRow, { label: "Priority" }, E(PriorityPill, { priority: priority })),
      risk === "High Risk" ? E(SideRow, { label: "Risk" },
        E("span", { style: { color: P.sCrit, fontWeight: 700 } }, "⚠ " + risk)
      ) : null,
      E(SideRow, { label: "Category" }, category),
      E(SideRow, { label: "Affected Users" },
        E("div", { style: { display: "flex", flexDirection: "column", gap: 3 } },
          affected.map(function(g, i) {
            return E("div", { key: i, style: { display: "flex", alignItems: "center", gap: 6 } },
              UserGroupIcon(g),
              E("span", null, g));
          })
        )
      ),
      wcag ? E(SideRow, { label: "Guidelines" },
        E("div", null,
          E("div", { style: { fontWeight: 700, color: P.slate } }, wcag + (wcagInfo.l ? " (WCAG 2.1 " + wcagInfo.l + ")" : "")),
          wcagInfo.n ? E("div", { style: { fontSize: 10, color: P.slateL, marginTop: 2, lineHeight: 1.4 } }, wcagInfo.n) : null
        )
      ) : null,
      E(SideRow, { label: "Client Status" },
        E("span", { style: { color: isOpen ? P.sCrit : P.emerald, fontWeight: 700 } }, isOpen ? "Open" : "Resolved")
      ),
      p.createdIn ? E(SideRow, { label: "Created In" }, p.createdIn) : null,
      p.updatedIn ? E(SideRow, { label: "Updated In" }, p.updatedIn) : null
    )
  );
}

export { AuditIssueCard };
